import React, { useState } from 'react';
import { useTranslation } from '../../context/LanguageContext';
import { useAuth } from '../../context/AuthContext';
import { FileSpreadsheet, CheckCircle2, AlertTriangle, ArrowRight, ShieldCheck, FileText } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://localhost/fhpf-api';

interface LegacyRow {
  memberId: string;
  month: string;
  amount: number;
  paidDate: string;
  note: string;
}

export const LegacyImport: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();

  const [csvText, setCsvText] = useState('');
  const [parsedRows, setParsedRows] = useState<LegacyRow[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [importing, setImporting] = useState(false);
  const [importReport, setImportReport] = useState<any | null>(null);

  const sampleCsv = `member_01,2024-01,5000,2024-01-08,January 2024 bank deposit slip #010
member_01,2024-02,5000,2024-02-09,February 2024 bank deposit slip #024
member_02,2024-01,5000,2024-01-07,January 2024 bank deposit slip #011
member_02,2024-02,5000,2024-02-08,February 2024 bank deposit slip #025`;

  const handleParseCsv = () => {
    setValidationError(null);
    setImportReport(null);

    const lines = csvText.trim().split('\n');
    if (lines.length === 0 || !lines[0].trim()) {
      setValidationError('Please paste or upload valid CSV content.');
      setParsedRows([]);
      return;
    }

    const rows: LegacyRow[] = [];
    const seen = new Set<string>();

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line || line.startsWith('#') || line.toLowerCase().startsWith('memberid')) continue;

      const parts = line.split(',').map((p) => p.trim());
      if (parts.length < 3) {
        setValidationError(`Line ${i + 1} has insufficient columns (expected: memberId, month, amount, paidDate, note).`);
        return;
      }

      const [memberId, month, amountStr, paidDate = '', note = ''] = parts;
      const amount = parseInt(amountStr, 10);

      if (!memberId || !/^\d{4}-\d{2}$/.test(month) || isNaN(amount) || amount <= 0) {
        setValidationError(`Line ${i + 1} has invalid data: memberId=${memberId}, month=${month}, amount=${amountStr}`);
        return;
      }

      const uniqueKey = `${memberId}_${month}`;
      if (seen.has(uniqueKey)) {
        setValidationError(`Duplicate entry found for member ${memberId} in month ${month}.`);
        return;
      }
      seen.add(uniqueKey);

      rows.push({
        memberId,
        month,
        amount,
        paidDate,
        note,
      });
    }

    if (rows.length === 0) {
      setValidationError('No valid historical data rows found in CSV.');
      return;
    }

    setParsedRows(rows);
  };

  const handleExecuteImport = async () => {
    if (!user || parsedRows.length === 0) return;

    if (!confirm(`Confirm import of ${parsedRows.length} historical contributions into the cryptographic ledger?`)) {
      return;
    }

    setImporting(true);
    setValidationError(null);

    try {
      const token = await user.getIdToken();
      const res = await fetch(`${API_BASE}/legacy-import`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ records: parsedRows }),
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error?.message || 'Legacy import failed.');
      }

      setImportReport(data);
      setParsedRows([]);
      setCsvText('');
    } catch (err: any) {
      setValidationError(err.message || 'Import failed.');
    } finally {
      setImporting(false);
    }
  };

  const fmt = (n: number) => `৳${n.toLocaleString('en-US')}`;
  const totalBatchAmount = parsedRows.reduce((sum, r) => sum + r.amount, 0);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <FileSpreadsheet className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          <span>Historical Legacy Import (14 Months)</span>
        </h2>
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Imports 14 months of verified physical bank contributions. Records are entered as <span className="font-mono text-primary-500">legacy_import</span> without fabricating artificial approval dates.
        </p>
      </div>

      {validationError && (
        <div className="p-4 rounded-xl bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-xs sm:text-sm text-red-700 dark:text-red-300 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{validationError}</span>
        </div>
      )}

      {importReport && (
        <div className="p-5 rounded-2xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900 text-xs sm:text-sm text-emerald-800 dark:text-emerald-200 space-y-2">
          <div className="flex items-center gap-2 font-bold text-base text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="w-5 h-5" />
            <span>Legacy Import Successfully Committed!</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-2">
            <div>
              <span className="block text-gray-500">Imported Records:</span>
              <span className="font-bold text-base">{importReport.importedCount}</span>
            </div>
            <div>
              <span className="block text-gray-500">Total Capital Credited:</span>
              <span className="font-bold text-base">{fmt(importReport.totalAmount)}</span>
            </div>
            <div>
              <span className="block text-gray-500">Ledger Block Range:</span>
              <span className="font-mono font-bold text-base">#{importReport.startSequence} – #{importReport.endSequence}</span>
            </div>
          </div>
          <div className="font-mono text-[11px] pt-1 text-gray-500 truncate">
            Final Hash: {importReport.finalHash}
          </div>
        </div>
      )}

      {/* CSV Input Area */}
      <div className="bg-white dark:bg-navy-900 rounded-2xl border border-gray-200/80 dark:border-navy-800 shadow-sm p-5 sm:p-6 space-y-4">
        <div className="flex items-center justify-between">
          <label className="block text-xs font-semibold uppercase tracking-wider text-gray-700 dark:text-gray-300">
            Paste CSV Data
          </label>
          <button
            type="button"
            onClick={() => setCsvText(sampleCsv)}
            className="text-xs text-primary-600 dark:text-primary-400 hover:underline font-semibold"
          >
            Load Sample Template
          </button>
        </div>

        <textarea
          rows={6}
          value={csvText}
          onChange={(e) => setCsvText(e.target.value)}
          placeholder="memberId,month,amount,paidDate,note"
          className="w-full p-3 font-mono text-xs rounded-xl border border-gray-300 dark:border-navy-700 bg-gray-50 dark:bg-navy-950 text-gray-900 dark:text-white outline-none focus:ring-2 focus:ring-primary-500"
        />

        <div className="flex items-center justify-between">
          <span className="text-xs text-gray-400">
            Columns: <code className="font-mono text-primary-500">memberId, month (YYYY-MM), amount, paidDate (YYYY-MM-DD), note</code>
          </span>
          <button
            type="button"
            onClick={handleParseCsv}
            className="px-4 py-2 rounded-xl bg-gray-900 hover:bg-black text-white dark:bg-navy-800 dark:hover:bg-navy-700 text-xs font-semibold"
          >
            {t('admin.previewBtn')}
          </button>
        </div>
      </div>

      {/* Preview Table */}
      {parsedRows.length > 0 && (
        <div className="bg-white dark:bg-navy-900 rounded-2xl border border-gray-200/80 dark:border-navy-800 shadow-sm overflow-hidden space-y-4">
          <div className="p-4 sm:p-5 border-b border-gray-100 dark:border-navy-800 flex items-center justify-between">
            <div>
              <h3 className="font-bold text-gray-900 dark:text-white text-sm">
                Validation Summary: {parsedRows.length} Rows Verified
              </h3>
              <p className="text-xs text-gray-500">
                Total batch capital: <span className="font-bold text-emerald-600">{fmt(totalBatchAmount)}</span>
              </p>
            </div>

            <button
              type="button"
              disabled={importing}
              onClick={handleExecuteImport}
              className="px-5 py-2.5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-semibold flex items-center gap-2 shadow-sm shadow-emerald-600/20 disabled:opacity-50"
            >
              <span>{importing ? 'Committing Batch...' : t('admin.importBtn')}</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          <div className="overflow-x-auto max-h-80">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50 dark:bg-navy-950/40 font-semibold uppercase text-gray-500 sticky top-0">
                <tr>
                  <th className="py-2.5 px-3">#</th>
                  <th className="py-2.5 px-3">Member ID</th>
                  <th className="py-2.5 px-3">Month</th>
                  <th className="py-2.5 px-3">Amount</th>
                  <th className="py-2.5 px-3">Historical Date</th>
                  <th className="py-2.5 px-3">Note</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-navy-800 font-mono">
                {parsedRows.map((r, i) => (
                  <tr key={i} className="hover:bg-gray-50/50 dark:hover:bg-navy-800/40">
                    <td className="py-2 px-3 text-gray-400">{i + 1}</td>
                    <td className="py-2 px-3 font-semibold text-gray-900 dark:text-white">{r.memberId}</td>
                    <td className="py-2 px-3 text-primary-600">{r.month}</td>
                    <td className="py-2 px-3 font-bold">{fmt(r.amount)}</td>
                    <td className="py-2 px-3 text-gray-500">{r.paidDate || 'N/A'}</td>
                    <td className="py-2 px-3 font-sans text-gray-600 dark:text-gray-300 truncate max-w-xs">{r.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};
